import React, { useEffect, useMemo, useState } from "react";
import { Link, Routes, Route, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Building2, Users2, Receipt, ClipboardList, Plus, Loader2,
  CalendarClock, AlertTriangle, CheckCircle2, ShieldCheck, Upload, FileText,
} from "lucide-react";
import BackButton from "@/components/business/BackButton";
import MetricCard from "@/components/business/MetricCard";
import PageHeader from "@/components/business/PageHeader";

const SECTIONS_26Q = ["194A", "194C", "194H", "194I", "194J", "194Q", "194R", "194S", "194T"];

export default function TDS() {
  return (
    <Routes>
      <Route index element={<TDSDashboard />} />
      <Route path="deductors" element={<Deductors />} />
      <Route path="deductees" element={<Deductees />} />
      <Route path="challans" element={<Challans />} />
      <Route path="returns" element={<Returns />} />
      <Route path="returns/:returnId" element={<ReturnDetail />} />
    </Routes>
  );
}

// ============ Dashboard ============
function TDSDashboard() {
  const [d, setD] = useState(null);
  const [cal, setCal] = useState(null);
  useEffect(() => {
    api.get("/tds/dashboard").then(({ data }) => setD(data));
    api.get("/tds/calendar").then(({ data }) => setCal(data));
  }, []);
  if (!d) return <AppShell><Loader2 className="h-5 w-5 animate-spin" /></AppShell>;

  const upcoming = (d.upcoming || []).slice(0, 6);
  const overdue = upcoming.filter(x => x.overdue);

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto">
        <BackButton to="/practice" label="Practice" />
        <PageHeader>
          <PageHeader.Title eyebrow="24Q · 26Q · corrections">TDS</PageHeader.Title>
        </PageHeader>
        <p className="mt-2 text-sm text-muted-foreground">Salary + non-salary TDS compliance — deductors, deductees, challans, quarterly returns, defaults &amp; corrections.</p>

        <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-4">
          <MetricCard label="Deductors" value={d.counts.deductors} icon={Building2} to="/tds/deductors" testId="tds-stat-deductors" />
          <MetricCard label="Deductees" value={d.counts.deductees} icon={Users2} to="/tds/deductees" testId="tds-stat-deductees" />
          <MetricCard label="Challans" value={d.counts.challans} icon={Receipt} to="/tds/challans" testId="tds-stat-challans" />
          <MetricCard label="Returns" value={d.counts.returns} icon={FileText} to="/tds/returns" testId="tds-stat-returns" />
          <MetricCard label="Unlinked" value={d.counts.unlinked_deductions} icon={AlertTriangle} tone={d.counts.unlinked_deductions > 0 ? "danger" : "default"} testId="tds-stat-unlinked" />
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Card className="p-6 border-border">
            <div className="flex items-center justify-between">
              <div className="font-display font-semibold text-lg flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" />Upcoming due dates</div>
              <Link to="/tds/returns" className="text-xs text-primary hover:underline">All returns →</Link>
            </div>
            {overdue.length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-rose-600 font-bold">Overdue ({overdue.length})</div>
                <ul className="mt-2 space-y-1.5">
                  {overdue.map(x => <UpcomingRow key={x.return_id} x={x} tone="danger" />)}
                </ul>
              </div>
            )}
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Next 45 days</div>
              <ul className="mt-2 space-y-1.5">
                {upcoming.filter(x => !x.overdue).map(x => <UpcomingRow key={x.return_id} x={x} />)}
                {upcoming.length === 0 && <div className="text-sm text-muted-foreground">No open returns.</div>}
              </ul>
            </div>
          </Card>

          <Card className="p-6 border-border">
            <div className="font-display font-semibold text-lg">Statutory calendar</div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              {(cal?.items || []).slice(0, 8).map((x, i) => (
                <div key={i} className={`rounded border p-2 ${x.days < 0 ? "border-border" : "border-primary/30"}`}>
                  <div className="font-semibold">{x.quarter} · AY {x.ay}</div>
                  <div className="text-muted-foreground">{x.due_date}</div>
                  <div className={`text-[10px] mt-0.5 ${x.days < 0 ? "text-muted-foreground" : "text-primary"}`}>
                    {x.days < 0 ? `${-x.days} d ago` : `${x.days} d away`}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <Button asChild size="sm" variant="outline"><Link to="/tds/deductors">+ Add deductor</Link></Button>
              <Button asChild size="sm" variant="outline"><Link to="/tds/deductees">+ Add deductee</Link></Button>
              <Button asChild size="sm" variant="outline"><Link to="/tds/challans">+ Add challan</Link></Button>
              <Button asChild size="sm"><Link to="/tds/returns">+ File a return</Link></Button>
            </div>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}


function UpcomingRow({ x, tone }) {
  return (
    <li className={`text-sm flex items-center justify-between rounded border px-3 py-1.5 ${tone === "danger" ? "border-rose-300 bg-rose-50/50" : "border-border"}`}>
      <div><span className="font-semibold">{x.type} {x.quarter}</span> · AY {x.assessment_year}</div>
      <div className="text-xs text-muted-foreground">{x.due_date} · {x.days < 0 ? `${-x.days}d overdue` : `${x.days}d`}</div>
    </li>
  );
}

// ============ Deductors ============
function Deductors() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", tan: "", pan: "", is_government: false });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/tds/deductors").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!f.name.trim() || !f.tan.trim()) return toast.error("Name and TAN required");
    setBusy(true);
    try {
      const payload = { ...f, tan: f.tan.toUpperCase(), pan: f.pan ? f.pan.toUpperCase() : undefined };
      if (!payload.pan) delete payload.pan;
      await api.post("/tds/deductors", payload);
      toast.success("Deductor created"); setOpen(false); setF({ name: "", tan: "", pan: "", is_government: false }); load();
    } catch (e) {
      const err = e.response?.data?.detail;
      toast.error(typeof err === "string" ? err : (err?.errors?.[0]?.message || "Failed"));
    } finally { setBusy(false); }
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto">
        <BackButton to="/tds" label="TDS" />
        <PageHeader>
          <PageHeader.Title>Deductors</PageHeader.Title>
          <PageHeader.Actions>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-deductor-btn"><Plus className="h-4 w-4 mr-1.5" />Add deductor</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add deductor</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs">Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-10 mt-1.5" data-testid="ded-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">TAN *</Label><Input value={f.tan} onChange={(e) => setF({ ...f, tan: e.target.value.toUpperCase() })} className="h-10 mt-1.5 font-mono uppercase" maxLength={10} data-testid="ded-tan" placeholder="DELH12345A" /></div>
                  <div><Label className="text-xs">PAN</Label><Input value={f.pan} onChange={(e) => setF({ ...f, pan: e.target.value.toUpperCase() })} className="h-10 mt-1.5 font-mono uppercase" maxLength={10} placeholder="AAACR1234C" /></div>
                </div>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.is_government} onChange={(e) => setF({ ...f, is_government: e.target.checked })} />Government deductor</label>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} disabled={busy} data-testid="ded-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </PageHeader.Actions>
        </PageHeader>
        <Card className="mt-5 border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              <tr><th className="text-left p-3">Name</th><th className="text-left p-3">TAN</th><th className="text-left p-3">PAN</th><th className="text-left p-3">Kind</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">No deductors yet.</td></tr>}
              {rows.map(r => (
                <tr key={r.id} className="border-t border-border">
                  <td className="p-3 font-medium">{r.name}</td>
                  <td className="p-3 font-mono uppercase">{r.tan}</td>
                  <td className="p-3 font-mono uppercase">{r.pan || "—"}</td>
                  <td className="p-3">{r.is_government ? "Govt" : "Non-govt"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </AppShell>
  );
}

// ============ Deductees (with bulk import) ============
function Deductees() {
  const [rows, setRows] = useState([]);
  const [deds, setDeds] = useState([]);
  const [dedId, setDedId] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [f, setF] = useState({ name: "", pan: "", deductor_id: "", kind: "vendor", default_section: "" });
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const params = {};
    if (dedId) params.deductor_id = dedId;
    if (q) params.q = q;
    const { data } = await api.get("/tds/deductees", { params });
    setRows(data);
  };
  useEffect(() => {
    api.get("/tds/deductors").then(({ data }) => setDeds(data));
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dedId]);

  const submit = async () => {
    if (!f.name.trim() || !f.deductor_id) return toast.error("Name and deductor required");
    setBusy(true);
    try {
      const payload = { ...f, pan: f.pan ? f.pan.toUpperCase() : undefined };
      if (!payload.pan) delete payload.pan;
      if (!payload.default_section) delete payload.default_section;
      await api.post("/tds/deductees", payload);
      toast.success("Deductee added"); setOpen(false); setF({ name: "", pan: "", deductor_id: "", kind: "vendor", default_section: "" }); load();
    } catch (e) {
      const err = e.response?.data?.detail;
      toast.error(typeof err === "string" ? err : (err?.errors?.[0]?.message || "Failed"));
    } finally { setBusy(false); }
  };

  const submitBulk = async () => {
    if (!dedId) return toast.error("Pick a deductor filter first (it's used as the target)");
    const lines = csv.split("\n").map(l => l.trim()).filter(Boolean);
    if (lines.length < 1) return toast.error("Paste CSV with at least one row");
    // Header: name,pan,section,kind
    let header = ["name", "pan", "section", "kind"];
    let dataLines = lines;
    if (/^name/i.test(lines[0])) { header = lines[0].split(",").map(s => s.trim().toLowerCase()); dataLines = lines.slice(1); }
    const rows = dataLines.map(l => {
      const cells = l.split(",").map(s => s.trim());
      const o = {};
      header.forEach((h, i) => { o[h === "section" ? "default_section" : h] = cells[i] || undefined; });
      return o;
    });
    setBusy(true);
    try {
      const { data } = await api.post("/tds/deductees/bulk", { deductor_id: dedId, rows });
      toast.success(`Imported ${data.created}/${data.total} deductees`);
      setBulkOpen(false); setCsv(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk import failed"); }
    finally { setBusy(false); }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto">
        <BackButton to="/tds" label="TDS" />
        <PageHeader>
          <PageHeader.Title>Deductees</PageHeader.Title>
          <PageHeader.Actions>
            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
              <DialogTrigger asChild><Button variant="outline" data-testid="bulk-btn"><Upload className="h-4 w-4 mr-1.5" />Bulk import</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Bulk import deductees</DialogTitle></DialogHeader>
                <div className="text-xs text-muted-foreground">Paste CSV with header row <code className="bg-muted px-1 rounded">name,pan,section,kind</code>. Rows without a valid PAN are still imported but marked for follow-up.</div>
                <div className="mt-3">
                  <Label className="text-xs">Target deductor</Label>
                  <Select value={dedId} onValueChange={setDedId}>
                    <SelectTrigger className="h-10 mt-1.5"><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent>{deds.map(d => <SelectItem key={d.id} value={d.id}>{d.name} · {d.tan}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <textarea value={csv} onChange={(e) => setCsv(e.target.value)} className="mt-3 w-full h-40 rounded border border-border p-2 font-mono text-xs" placeholder="name,pan,section,kind&#10;Aarav,ABCPM1234A,192,employee&#10;XYZ Contractors,ABCFM4567B,194C,contractor" data-testid="csv-input" />
                <DialogFooter>
                  <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
                  <Button onClick={submitBulk} disabled={busy || !dedId}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Import"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v && dedId) setF(f => ({ ...f, deductor_id: dedId })); }}>
              <DialogTrigger asChild><Button data-testid="add-dee-btn"><Plus className="h-4 w-4 mr-1.5" />Add deductee</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add deductee</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label className="text-xs">Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-10 mt-1.5" data-testid="dee-name" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">PAN</Label><Input value={f.pan} onChange={(e) => setF({ ...f, pan: e.target.value.toUpperCase() })} className="h-10 mt-1.5 font-mono uppercase" maxLength={10} /></div>
                    <div>
                      <Label className="text-xs">Deductor</Label>
                      <Select value={f.deductor_id} onValueChange={(v) => setF({ ...f, deductor_id: v })}>
                        <SelectTrigger className="h-10 mt-1.5" data-testid="dee-deductor"><SelectValue placeholder="Choose…" /></SelectTrigger>
                        <SelectContent>{deds.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Kind</Label>
                      <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
                        <SelectTrigger className="h-10 mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["employee", "vendor", "contractor", "professional", "other"].map(x => <SelectItem key={x} value={x} className="capitalize">{x}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">Default section</Label><Input value={f.default_section} onChange={(e) => setF({ ...f, default_section: e.target.value.toUpperCase() })} className="h-10 mt-1.5 font-mono" placeholder="192 / 194C / 194J" /></div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button onClick={submit} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </PageHeader.Actions>
        </PageHeader>

        <Card className="mt-5 p-4 border-border flex flex-wrap gap-2 items-center">
          <Select value={dedId || "all"} onValueChange={(v) => setDedId(v === "all" ? "" : v)}>
            <SelectTrigger className="h-10 w-72"><SelectValue placeholder="Filter deductor" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All deductors</SelectItem>
              {deds.map(d => <SelectItem key={d.id} value={d.id}>{d.name} · {d.tan}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search name / PAN" className="h-10 max-w-xs" />
          <Button variant="outline" size="sm" onClick={load}>Search</Button>
        </Card>

        <Card className="mt-5 border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              <tr><th className="text-left p-3">Name</th><th className="text-left p-3">PAN</th><th className="text-left p-3">Kind</th><th className="text-left p-3">Section</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">No deductees.</td></tr>}
              {rows.map(r => (
                <tr key={r.id} className="border-t border-border">
                  <td className="p-3 font-medium">{r.name}</td>
                  <td className="p-3 font-mono uppercase">{r.pan || <span className="text-rose-600">missing</span>}</td>
                  <td className="p-3 capitalize">{r.kind}</td>
                  <td className="p-3 font-mono">{r.default_section || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </AppShell>
  );
}

// ============ Challans ============
function Challans() {
  const [rows, setRows] = useState([]);
  const [deds, setDeds] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ deductor_id: "", bsr_code: "", challan_no: "", paid_on: "", amount: 0, tds_amount: 0 });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/tds/challans").then(({ data }) => setRows(data));
  useEffect(() => { load(); api.get("/tds/deductors").then(({ data }) => setDeds(data)); }, []);

  const submit = async () => {
    if (!f.deductor_id || !f.bsr_code || !f.challan_no || !f.paid_on) return toast.error("Fill all fields");
    setBusy(true);
    try {
      await api.post("/tds/challans", { ...f, amount: Number(f.amount), tds_amount: Number(f.tds_amount) });
      toast.success("Challan added"); setOpen(false);
      setF({ deductor_id: "", bsr_code: "", challan_no: "", paid_on: "", amount: 0, tds_amount: 0 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto">
        <BackButton to="/tds" label="TDS" />
        <PageHeader>
          <PageHeader.Title>Challans</PageHeader.Title>
          <PageHeader.Actions>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-challan-btn"><Plus className="h-4 w-4 mr-1.5" />Add challan</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add TDS challan</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Deductor</Label>
                  <Select value={f.deductor_id} onValueChange={(v) => setF({ ...f, deductor_id: v })}>
                    <SelectTrigger className="h-10 mt-1.5"><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent>{deds.map(d => <SelectItem key={d.id} value={d.id}>{d.name} · {d.tan}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">BSR code (7 digits)</Label><Input value={f.bsr_code} onChange={(e) => setF({ ...f, bsr_code: e.target.value })} className="h-10 mt-1.5 font-mono" maxLength={7} /></div>
                  <div><Label className="text-xs">Challan number</Label><Input value={f.challan_no} onChange={(e) => setF({ ...f, challan_no: e.target.value })} className="h-10 mt-1.5 font-mono" /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="text-xs">Paid on</Label><Input type="date" value={f.paid_on} onChange={(e) => setF({ ...f, paid_on: e.target.value })} className="h-10 mt-1.5" /></div>
                  <div><Label className="text-xs">Amount</Label><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="h-10 mt-1.5" /></div>
                  <div><Label className="text-xs">TDS portion</Label><Input type="number" value={f.tds_amount} onChange={(e) => setF({ ...f, tds_amount: e.target.value })} className="h-10 mt-1.5" /></div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </PageHeader.Actions>
        </PageHeader>
        <Card className="mt-5 border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              <tr><th className="text-left p-3">Paid on</th><th className="text-left p-3">BSR</th><th className="text-left p-3">Challan #</th><th className="text-right p-3">Amount</th><th className="text-right p-3">TDS</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No challans.</td></tr>}
              {rows.map(r => (
                <tr key={r.id} className="border-t border-border">
                  <td className="p-3">{r.paid_on}</td>
                  <td className="p-3 font-mono">{r.bsr_code}</td>
                  <td className="p-3 font-mono">{r.challan_no}</td>
                  <td className="p-3 text-right font-mono">₹{Number(r.amount || 0).toLocaleString()}</td>
                  <td className="p-3 text-right font-mono">₹{Number(r.tds_amount || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </AppShell>
  );
}

// ============ Returns ============
function Returns() {
  const [rows, setRows] = useState([]);
  const [deds, setDeds] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ deductor_id: "", return_type: "24Q", quarter: "Q1", assessment_year: "2025-26" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/tds/returns").then(({ data }) => setRows(data));
  useEffect(() => { load(); api.get("/tds/deductors").then(({ data }) => setDeds(data)); }, []);

  const submit = async () => {
    if (!f.deductor_id) return toast.error("Pick a deductor");
    setBusy(true);
    try {
      await api.post("/tds/returns", f);
      toast.success("Return draft created"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto">
        <BackButton to="/tds" label="TDS" />
        <PageHeader>
          <PageHeader.Title>TDS Returns</PageHeader.Title>
          <PageHeader.Actions>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-return-btn"><Plus className="h-4 w-4 mr-1.5" />New return</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Start a new TDS return</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Deductor</Label>
                  <Select value={f.deductor_id} onValueChange={(v) => setF({ ...f, deductor_id: v })}>
                    <SelectTrigger className="h-10 mt-1.5"><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent>{deds.map(d => <SelectItem key={d.id} value={d.id}>{d.name} · {d.tan}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Type</Label>
                    <Select value={f.return_type} onValueChange={(v) => setF({ ...f, return_type: v })}>
                      <SelectTrigger className="h-10 mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="24Q">24Q — Salary</SelectItem>
                        <SelectItem value="26Q">26Q — Non-salary</SelectItem>
                        <SelectItem value="27Q">27Q — Non-resident payees</SelectItem>
                        <SelectItem value="27EQ">27EQ — TCS</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Quarter</Label>
                    <Select value={f.quarter} onValueChange={(v) => setF({ ...f, quarter: v })}>
                      <SelectTrigger className="h-10 mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{["Q1", "Q2", "Q3", "Q4"].map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">AY</Label><Input value={f.assessment_year} onChange={(e) => setF({ ...f, assessment_year: e.target.value })} className="h-10 mt-1.5" /></div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create draft"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </PageHeader.Actions>
        </PageHeader>
        <Card className="mt-5 border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              <tr><th className="text-left p-3">Type</th><th className="text-left p-3">Quarter</th><th className="text-left p-3">AY</th><th className="text-left p-3">Status</th><th></th></tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No returns yet.</td></tr>}
              {rows.map(r => (
                <tr key={r.id} className="border-t border-border">
                  <td className="p-3 font-semibold">{r.return_type}{r.is_correction && <Badge variant="outline" className="ml-2 text-[10px]">correction</Badge>}</td>
                  <td className="p-3">{r.quarter}</td>
                  <td className="p-3">{r.assessment_year}</td>
                  <td className="p-3 capitalize"><Badge variant="outline">{r.status}</Badge></td>
                  <td className="p-3 text-right"><Button asChild variant="outline" size="sm"><Link to={`/tds/returns/${r.id}`}>Open</Link></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </AppShell>
  );
}

// ============ Return detail ============
function ReturnDetail() {
  const { returnId } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [issues, setIssues] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/tds/returns/${returnId}`).then(({ data }) => setD(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [returnId]);

  const validate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tds/returns/${returnId}/validate`);
      setIssues(data);
      toast.success(`Validation: ${data.counts.critical} critical, ${data.counts.error} error, ${data.counts.warning} warning`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const finalize = async () => {
    setBusy(true);
    try { await api.post(`/tds/returns/${returnId}/finalize`); toast.success("Return prepared"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const correction = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tds/returns/${returnId}/correction`);
      toast.success("Correction return created"); nav(`/tds/returns/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!d) return <AppShell><Loader2 className="h-5 w-5 animate-spin" /></AppShell>;
  const r = d.return; const ctx = d.context;
  const bySeverity = { critical: [], error: [], warning: [], info: [] };
  (r.issues || []).forEach(x => bySeverity[x.severity]?.push(x));

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto">
        <BackButton to="/tds/returns" label="Returns" />
        <PageHeader>
          <div>
            <PageHeader.Title eyebrow={`${r.return_type} · ${r.quarter} · AY ${r.assessment_year}`}>TDS Return · {r.return_type}</PageHeader.Title>
            <div className="mt-2 flex gap-2 items-center">
              <Badge variant="outline" className="capitalize">{r.status}</Badge>
              {r.is_correction && <Badge variant="outline" className="border-amber-300 text-amber-700">Correction</Badge>}
              {r.due_date && <span className="text-xs text-muted-foreground">Due {r.due_date}</span>}
            </div>
          </div>
          <PageHeader.Actions>
            <Button onClick={validate} disabled={busy} variant="outline" data-testid="validate-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1.5" />}Validate</Button>
            {r.status === "validated" && <Button onClick={finalize} disabled={busy} data-testid="finalize-btn"><CheckCircle2 className="h-4 w-4 mr-1.5" />Finalize</Button>}
            {["prepared", "filed"].includes(r.status) && <Button variant="outline" onClick={correction} disabled={busy}>Create correction</Button>}
          </PageHeader.Actions>
        </PageHeader>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5">
          <Card className="p-6 border-border">
            <div className="font-display font-semibold text-lg">Return contents</div>
            <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
              <div><div className="text-xs text-muted-foreground">Deductor</div><div className="font-medium">{ctx.deductor?.name}</div><div className="font-mono text-xs uppercase">{ctx.tan}</div></div>
              <div><div className="text-xs text-muted-foreground">Deductees</div><div className="font-display text-2xl font-bold">{ctx.deductees?.length || 0}</div></div>
              <div><div className="text-xs text-muted-foreground">Challans</div><div className="font-display text-2xl font-bold">{ctx.challans?.length || 0}</div></div>
            </div>

            {(ctx.deductees || []).length > 0 && (
              <div className="mt-5">
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Deductees</div>
                <table className="mt-2 w-full text-xs">
                  <thead className="bg-muted/40">
                    <tr><th className="text-left p-2">PAN</th><th className="text-left p-2">Name</th><th className="text-left p-2">Sec</th><th className="text-right p-2">Gross</th><th className="text-right p-2">TDS</th></tr>
                  </thead>
                  <tbody>
                    {ctx.deductees.map((x, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="p-2 font-mono uppercase">{x.pan || "—"}</td>
                        <td className="p-2">{x.name}</td>
                        <td className="p-2 font-mono">{x.section || "—"}</td>
                        <td className="p-2 text-right font-mono">₹{Number(x.gross_amount || 0).toLocaleString()}</td>
                        <td className="p-2 text-right font-mono">₹{Number(x.tds_amount || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card className="p-5 border-border h-fit">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Validation issues</div>
            {(r.issues || []).length === 0 ? (
              <div className="mt-3 text-sm text-muted-foreground">Not yet validated. Click Validate to run the Rules Engine.</div>
            ) : (
              <div className="mt-3 space-y-3">
                {["critical", "error", "warning", "info"].map(sev => bySeverity[sev].length > 0 && (
                  <div key={sev}>
                    <div className={`text-[10px] uppercase tracking-[0.18em] font-bold ${sev === "critical" ? "text-rose-700" : sev === "error" ? "text-rose-600" : sev === "warning" ? "text-amber-700" : "text-muted-foreground"}`}>{sev} ({bySeverity[sev].length})</div>
                    <ul className="mt-1 space-y-1.5">
                      {bySeverity[sev].map((x, i) => (
                        <li key={i} className="text-xs rounded border border-border p-2">
                          <div className="font-mono text-[10px] text-muted-foreground">{x.code}</div>
                          <div className="mt-0.5">{x.message}</div>
                          {x.fix && <div className="mt-1 text-muted-foreground italic">{x.fix}</div>}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
